import Modal from "@via/components/modal";
import {
  SpreadsheetData,
  SpreadsheetEditor,
} from "@via/components/spreadsheet-editor/spreadsheet-editor";
import { ModalSize } from "@via/utils/constants";
import { useEffect, useState } from "react";
import { useUpdateVmetaData } from "@via/pages/projects/metadata/metadata.api.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { NOTIFICATION } from "@via/context/notifications-context";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { VmetaDataResponseBody, IMetaFieldTypes } from "common";
import {
  processError,
  formatSpreadsheetCellValue,
  getInitialColumnVisibility,
  applyDefaultHiddenColumns,
  getAllVisibleColumnVisibility,
} from "@via/pages/data/premium-dataset/utils/helper";

interface EditCollectionFileProps {
  collectionFiles: {
    data: VmetaDataResponseBody[];
    meta: {
      field_types: Record<string, unknown>[];
    };
  };
  canvasId: string;
  collectionName: string;
  nonEditableItemLabels?: string[];
  datasetUsage?: {
    datasetLabel: string;
    fileLabels: string[];
    datasetId: string;
  }[];
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const EditCollectionFile = ({
  collectionFiles,
  canvasId,
  collectionName,
  nonEditableItemLabels = [],
  datasetUsage = [],
  isLoading,
  isOpen,
  onClose,
}: EditCollectionFileProps) => {
  const { setNotification } = useNotifications();
  const [labels, setLabels] = useState<IMetaFieldTypes[]>([]);
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData>([]);
  const [errors, setErrors] = useState([]);
  const [changedRows, setChangedRows] = useState<Set<number>>(new Set());
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const { mutateAsync: updateVmetaData, isPending: isUpdateLoading } =
    useUpdateVmetaData();

  useEffect(() => {
    const allLabels =
      collectionFiles.meta?.field_types.filter(
        fieldType => !fieldType.hasNamingPattern
      ) || ([] as IMetaFieldTypes[]);

    const allSpreadsheetData = collectionFiles.data.map(file => {
      return (
        allLabels?.map(label => {
          const value = file[label.name as string];
          return formatSpreadsheetCellValue(value);
        }) || []
      );
    });

    setLabels(allLabels as unknown as IMetaFieldTypes[]);
    setSpreadsheetData(allSpreadsheetData);

    // Auto-calculate visibility
    if (allSpreadsheetData.length > 0) {
      const labelNames = allLabels.map(l => l.label);
      let initialVisibility = getInitialColumnVisibility(
        labelNames as string[],
        allSpreadsheetData
      );

      // For file collection, default-hide some columns regardless of data,
      // and default-show the rest (matching Premium Dataset behavior).
      if (collectionName === "file") {
        const labelsAsStrings = labelNames as string[];
        initialVisibility = applyDefaultHiddenColumns(
          getAllVisibleColumnVisibility(labelsAsStrings),
          labelsAsStrings
        );
      }

      setColumnVisibility(initialVisibility);
    }
  }, [collectionFiles, collectionName]);

  const updateSpreadsheetData = (
    data: SpreadsheetData,
    rowIndex?: number,
    rowIndices?: number[]
  ) => {
    setSpreadsheetData(data);
    const changedRowIndices =
      rowIndices && rowIndices.length > 0
        ? rowIndices
        : rowIndex !== undefined
          ? [rowIndex]
          : [];
    if (changedRowIndices.length === 0) {
      return;
    }
    setChangedRows(prev => new Set([...prev, ...changedRowIndices]));
  };

  const handleConfirm = async () => {
    const transformedDatas: any = [];
    const rowErrors = [];

    try {
      const changedRowsArray = Array.from(changedRows);

      changedRowsArray.forEach(rowIndex => {
        const row = spreadsheetData[rowIndex];
        const transformedData: any = {};

        row.forEach((cell, colIndex) => {
          const label = labels[colIndex];
          const labelName = label?.label;
          // Only include visible columns
          if (label && labelName && columnVisibility[labelName] !== false) {
            const stringValue = cell.value?.toString() ?? "";
            const value = stringValue === "" ? null : stringValue;
            transformedData[labelName] =
              value != null && (value.startsWith("{") || value.startsWith("["))
                ? JSON.parse(value)
                : value;
          }
        });
        // vmeta data routes use /v1/vmeta/canvas/:canvasId/...
        transformedData["canvasId"] = canvasId;
        transformedData["collectionName"] = collectionName;
        transformedData["id"] =
          (collectionFiles.data[rowIndex] as { _id?: string })?._id || "";
        transformedDatas.push(transformedData);
      });

      const results = await Promise.allSettled(
        transformedDatas.map(async transformedData => {
          return await updateVmetaData(transformedData);
        })
      );

      const errorRowIndices = new Set<number>();

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          processError(result.reason, changedRowsArray[index], rowErrors);
          errorRowIndices.add(changedRowsArray[index]);
        } else if (result.status === "fulfilled" && "error" in result.value) {
          processError(result.value.error, changedRowsArray[index], rowErrors);
          errorRowIndices.add(changedRowsArray[index]);
        }
      });

      if (rowErrors.length) {
        setChangedRows(errorRowIndices);
        setErrors(rowErrors);
      } else {
        setChangedRows(new Set());
        onClose();
      }
    } catch (_error) {
      setNotification({
        id: "error-transforming-data",
        message: "Error transforming data",
        details: "Please ensure the data is in the correct format.",
        variant: NOTIFICATION.ERROR,
      });
      return;
    }
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title="Edit Data"
      showButtons={false}
      isLoading={isLoading}
      size={ModalSize.Large}
      width={1000}>
      <>
        <MessageBanner messages={errors} type={BannerType.Error} />
        <div className="mb-5">
          <SpreadsheetEditor
            columnLabels={labels.map(label => label?.label)}
            data={spreadsheetData}
            onChange={updateSpreadsheetData}
            isLoading={isUpdateLoading}
            disableContextMenu
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />

          {nonEditableItemLabels.length > 0 && (
            <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              <div className="font-semibold">Info</div>
              <div className="mt-1">
                You don&apos;t have permission to edit the following items:{" "}
                <strong className="font-semibold">
                  {Array.from(new Set(nonEditableItemLabels)).join(", ")}
                </strong>
                .
              </div>
            </div>
          )}

          {collectionName === "file" && datasetUsage.length > 0 && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="font-semibold">Warning</div>
              <div className="mt-1">
                Some of these files are referenced by datasets. If you update
                them, this will change the dataset contents listed below.
              </div>
              <div className="mt-2">
                <strong className="font-semibold">Datasets: </strong>
                {Array.from(
                  new Set(datasetUsage.map(d => d.datasetLabel).filter(Boolean))
                ).join(", ")}
                .
              </div>
              <details className="mt-3">
                <summary className="cursor-pointer select-none text-slate-700">
                  Show which files are used in which dataset
                </summary>
                <div className="mt-2 space-y-2">
                  {datasetUsage.map(ds => (
                    <div key={ds.datasetId} className="text-slate-700">
                      <strong className="font-semibold">
                        {ds.datasetLabel}
                      </strong>
                      {ds.fileLabels?.length ? (
                        <>: {ds.fileLabels.join(", ")}</>
                      ) : null}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              id="update-button"
              type="button"
              disabled={isUpdateLoading}
              onClick={e => {
                e.preventDefault();
                handleConfirm();
              }}
              className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Update
            </button>
            <button
              id="cancel-button"
              type="button"
              className="ml-4 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </>
    </Modal>
  );
};

export default EditCollectionFile;
